import { Form, Space } from 'antd'
import CIMSButton from 'components/CIMSButton'
import CIMSDrawer from 'components/CIMSDrawer'
import { useRecipientSelectContext } from 'components/CIMSSelect/common/customWorkflows/RecipientSelect/RecipientSelectContext'
import type { Recipient } from 'components/CIMSSelect/common/customWorkflows/RecipientSelect/types'
import { useEffect, useMemo, useRef } from 'react'
import type {
  Notification,
  NotificationField,
  NotificationStatus,
  NotificationTemplate,
} from '../../types'
import Conditions from './components/Conditions'
import SendSettings from './components/SendSettings'
import TriggerSettings from './components/TriggerSettings'
import {
  toFormValues as toTriggerFormValues,
  toSubmission as toTriggerSubmission,
} from './components/TriggerSettings/utils/normalization'
import {
  toFormValues as toConditionFormValues,
  toSubmission as toConditionSubmission,
} from './components/Conditions/utils/normalization'
import { labeledValueString } from './utils/referenceValues'
import styles from './index.module.css'
import type {
  NotificationFormSubmission,
  NotificationFormValues,
} from './types'

const NOTIFICATION_FORM_ID = 'notification-form'

function toRecipientSelectValue(
  recipient: Notification['to'][number]
): Recipient {
  if (recipient.__typename === 'User')
    return { ...recipient, id: recipient.userId }

  return { ...recipient, id: recipient.nonUserEmailId }
}

type NotificationDrawerProps = {
  fields: NotificationField[]
  loading: boolean
  notification: Notification | null
  onClose: () => void
  onSubmit: (submission: NotificationFormSubmission) => Promise<void> | void
  open: boolean
  statuses: NotificationStatus[]
  templates: NotificationTemplate[]
}

function NotificationDrawer({
  fields,
  loading,
  notification,
  onClose,
  onSubmit,
  open,
  statuses,
  templates,
}: NotificationDrawerProps) {
  const [form] = Form.useForm<NotificationFormValues>()
  const activeSession = useRef<string | null>(null)
  const { fromLabeledValues, toLabeledValues } = useRecipientSelectContext()
  const actionLabel = notification ? 'Edit Notification' : 'Create Notification'
  const initialValues = useMemo<NotificationFormValues>(
    () => ({
      ...toTriggerFormValues(
        notification
          ? {
              trigger: notification.trigger,
              triggerSettings: notification.triggerSettings,
            }
          : null,
        { fields, statuses }
      ),
      conditions: toConditionFormValues(notification?.conditions ?? [], {
        fields,
        statuses,
      }),
      templateId: notification
        ? {
            deleted: notification.notificationTemplate.deleted,
            label: notification.notificationTemplate.name,
            value: notification.templateId,
          }
        : undefined,
      to: toLabeledValues(notification?.to.map(toRecipientSelectValue)),
    }),
    [fields, notification, statuses, toLabeledValues]
  )

  useEffect(() => {
    if (!open) {
      activeSession.current = null
      return
    }
    const session = notification?.id ?? 'create'
    if (activeSession.current === session) return

    activeSession.current = session
    form.resetFields()
  }, [form, initialValues, notification?.id, open])

  return (
    <CIMSDrawer
      footer={
        <div className={styles.footer}>
          <Space>
            <CIMSButton onClick={onClose}>Cancel</CIMSButton>
            <CIMSButton
              form={NOTIFICATION_FORM_ID}
              htmlType="submit"
              loading={loading}
              type="primary"
            >
              {actionLabel}
            </CIMSButton>
          </Space>
        </div>
      }
      onClose={onClose}
      open={open}
      title={actionLabel}
    >
      <Form<NotificationFormValues>
        form={form}
        id={NOTIFICATION_FORM_ID}
        initialValues={initialValues}
        layout="vertical"
        onFinish={(values) => {
          const triggerSubmission = toTriggerSubmission(values)
          const conditions = toConditionSubmission(values.conditions)
          const templateId = labeledValueString(values.templateId)
          if (triggerSubmission && conditions && templateId)
            onSubmit({
              ...triggerSubmission,
              conditions,
              templateId,
              to: fromLabeledValues(values.to),
            })
        }}
        onValuesChange={(changedValues) => {
          if (Object.prototype.hasOwnProperty.call(changedValues, 'trigger'))
            form.setFieldsValue({ templateId: undefined })
        }}
      >
        <TriggerSettings fields={fields} statuses={statuses} />
        <Conditions fields={fields} statuses={statuses} />
        <SendSettings
          fields={fields}
          statuses={statuses}
          templates={templates}
        />
      </Form>
    </CIMSDrawer>
  )
}

export default NotificationDrawer
